/**
 * Блок «Административное деление» (FE-7, redesign по макету).
 *
 * Иерархический список территорий: с.о. → сёла, раскрыт по умолчанию
 * (шеврон сворачивает поддерево), с горизонтальными барами величин и
 * значением справа. Цвет бара — по
 * глубине вложенности (0 — синий, 1 — зелёный, 2+ — песочный), ширина —
 * доля от максимума своего уровня. Шапка — «Население (человек)» с общим
 * итогом; футер «Общий рост населения» рендерится, только если процент
 * передан (API его пока не отдаёт).
 *
 * Источник данных — `PublicGeneralResponse.children` (BE-7): сейчас это
 * один уровень без вложенности — тогда все строки синие и без шевронов;
 * `children` у элемента появятся вместе с иерархией на BE.
 *
 * Если список пуст — placeholder «нет данных».
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { cn } from '../../../../lib/cn';
import type { ChildKato } from '../../../../types/public';

export interface AdminDivisionItem extends ChildKato {
  children?: AdminDivisionItem[];
}

interface Props {
  items: AdminDivisionItem[];
  /** Подпись периода данных в правом углу заголовка (например, год). */
  periodLabel?: string | null;
  /** Рост населения в %, футер блока; null/undefined — футер скрыт. */
  growthPercent?: number | null;
}

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);

/** Цвет бара по глубине вложенности. */
const BAR_COLORS = [
  'bg-chart-blue',
  'bg-chart-green',
  'bg-chart-sand',
] as const;
const barColor = (depth: number) =>
  BAR_COLORS[Math.min(depth, BAR_COLORS.length - 1)];

interface FlatRow {
  node: AdminDivisionItem;
  depth: number;
}

/**
 * Разворачивает дерево в видимые строки. По умолчанию всё раскрыто —
 * скрываются только поддеревья узлов, свёрнутых пользователем.
 */
const flattenVisible = (
  nodes: AdminDivisionItem[],
  collapsed: ReadonlySet<number>,
  depth = 0,
): FlatRow[] =>
  nodes.flatMap((node) => [
    { node, depth },
    ...(node.children && !collapsed.has(node.kato)
      ? flattenVisible(node.children, collapsed, depth + 1)
      : []),
  ]);

/** Максимум населения на каждом уровне — для нормировки ширины баров. */
const maxByDepth = (
  nodes: AdminDivisionItem[],
  depth = 0,
  acc: number[] = [],
): number[] => {
  for (const node of nodes) {
    acc[depth] = Math.max(acc[depth] ?? 0, node.population ?? 0);
    if (node.children) maxByDepth(node.children, depth + 1, acc);
  }
  return acc;
};

export const AdministrativeDivision = ({ items, growthPercent }: Props) => {
  const { t, i18n } = useTranslation();
  const isKz = i18n.language === 'kz';

  // Список раскрыт по умолчанию: храним СВЁРНУТЫЕ пользователем узлы.
  const [collapsedNodes, setCollapsedNodes] = useState<ReadonlySet<number>>(
    new Set(),
  );
  const toggle = (kato: number) =>
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(kato)) {
        next.delete(kato);
      } else {
        next.add(kato);
      }
      return next;
    });

  const rows = flattenVisible(items, collapsedNodes);
  const maxima = useMemo(() => maxByDepth(items), [items]);

  return (
    <Card className="h-full shadow-card">
      <CardHeader className="flex-row items-baseline justify-between space-y-0">
        <CardTitle>
          {t('digitalPassport.administrativeDivision.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <section data-testid="block-administrative-division">
          {items.length === 0 ? (
            <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
          ) : (
            <>
              <ul
                className="max-h-[360px] overflow-y-auto"
                data-testid="admin-division-list"
              >
                {rows.map(({ node, depth }, index) => {
                  const hasChildren = (node.children?.length ?? 0) > 0;
                  const isExpanded = !collapsedNodes.has(node.kato);
                  const max = maxima[depth] || 1;
                  const widthPct =
                    node.population === null
                      ? 0
                      : Math.max(4, (node.population / max) * 100);
                  return (
                    <li
                      key={node.kato}
                      data-testid={`admin-row-${node.kato}`}
                      className={cn(
                        'flex items-center gap-2 rounded-md py-2 pr-2 text-sm',
                        index % 2 === 1 && 'bg-surface-muted/60',
                      )}
                      style={{ paddingLeft: `${depth * 22 + 4}px` }}
                    >
                      <span className="flex min-w-0 flex-1 basis-2/5 items-center gap-1">
                        {hasChildren ? (
                          <button
                            type="button"
                            onClick={() => toggle(node.kato)}
                            aria-expanded={isExpanded}
                            aria-label={isKz ? node.name_kz : node.name_ru}
                            className="shrink-0 rounded p-0.5 text-text hover:bg-surface-muted"
                          >
                            {isExpanded ? (
                              <ChevronDown className="size-4" />
                            ) : (
                              <ChevronRight className="size-4" />
                            )}
                          </button>
                        ) : (
                          <span className="w-5 shrink-0" />
                        )}
                        <span className="truncate">
                          {isKz ? node.name_kz : node.name_ru}
                        </span>
                      </span>
                      <span className="flex-1 basis-2/5">
                        {node.population !== null && (
                          <span
                            className={cn(
                              'block h-4 rounded-sm',
                              barColor(depth),
                            )}
                            style={{ width: `${widthPct}%` }}
                            data-testid={`admin-row-${node.kato}-bar`}
                          />
                        )}
                      </span>
                      <span
                        data-testid={`admin-row-${node.kato}-population`}
                        className={cn(
                          'w-16 shrink-0 text-right tabular-nums',
                          depth === 0 && 'font-bold',
                        )}
                      >
                        {node.population !== null ? fmt(node.population) : '—'}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {growthPercent != null && (
                <div
                  className="flex items-center justify-between border-t border-surface-border pt-3"
                  data-testid="admin-growth"
                >
                  <span className="text-sm font-bold">
                    {t('digitalPassport.administrativeDivision.growth')}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm font-extrabold text-brand-600">
                    <TrendingUp className="size-4" />
                    {Intl.NumberFormat('ru-RU', {
                      maximumFractionDigits: 1,
                    }).format(growthPercent)}
                    %
                  </span>
                </div>
              )}
            </>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
